import React, { FC } from "react";
import { Button, Card, Col, Row, Typography, Upload, Progress, Space, Alert, Input, Divider } from "antd";
import styled from "@emotion/styled";
import { UploadOutlined, CheckCircleOutlined, LoadingOutlined, LinkOutlined } from "@ant-design/icons";

import { StledSpin } from "../custom-components";
import { TabComponentProps } from "../utils/common.interface";
import useVideoComplianceUploadHook from "./hooks/useVideoComplianceUploadHook";
import useVideoUrlIngestHook from "./hooks/useVideoUrlIngestHook";

const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

const Container = styled.div`
	padding: 48px 24px;
	max-width: 1000px;
	margin: 0 auto;
`;


const UploadCard = styled(Card)`
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	border: none;
	
	.ant-card-body {
		padding: 40px;
	}
`;



const ProgressContainer = styled.div`
	margin-top: 24px;
	padding: 24px;
	background: #f5f5f5;
	border-radius: 8px;
`;

const StatusText = styled(Text)`
	font-size: 14px;
	font-weight: 500;
	display: block;
	margin-bottom: 8px;
`;

const FileInfo = styled.div`
	margin-top: 16px;
	padding: 12px;
	background: #f9f9f9;
	border-radius: 4px;
`;

const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const UrlInputContainer = styled.div`
	margin-top: 24px;
	padding: 24px;
	background: #f9f9f9;
	border-radius: 8px;
`;

const VideoComplianceUpload: FC<TabComponentProps> = () => {
	const {
		loading: fileUploadLoading,
		file,
		fileList,
		progress,
		handleUpload,
		handleFileRemove,
		beforeUpload,
	} = useVideoComplianceUploadHook();

	const {
		loading: urlIngestLoading,
		url,
		handleUrlChange,
		handleIngest,
	} = useVideoUrlIngestHook();

	const loading = fileUploadLoading || urlIngestLoading;

	const uploadProps = {
		onRemove: handleFileRemove,
		beforeUpload: beforeUpload,
		fileList,
		accept: ".mp4,.mov,.avi,.mkv,.webm",
		multiple: false,
		showUploadList: {
			showPreviewIcon: false,
			showRemoveIcon: true,
		},
	};

	const getStatusIcon = () => {
		switch (progress.status) {
			case "uploading":
			case "completing":
				return <LoadingOutlined spin style={{ color: "#1890ff" }} />;
			case "success":
				return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
			case "error":
				return null;
			default:
				return null;
		}
	};

	const getStatusText = () => {
		switch (progress.status) {
			case "uploading":
				return `Uploading... ${progress.percentage}%`;
			case "completing":
				return "Completing upload...";
			case "success":
				return "Upload completed successfully!";
			case "error":
				return `Upload failed: ${progress.error || "Unknown error"}`;
			default:
				return "Ready to upload";
		}
	};

	return (
		<StledSpin spinning={loading}>
			<Container>

				<Row justify="center">
					<Col xs={24} sm={24} md={20} lg={18} xl={16}>
						<UploadCard>
							<Dragger {...uploadProps}>
								<Space direction="vertical" size="large" align="center" style={{ width: "100%" }}>
									<UploadOutlined style={{ fontSize: 64, color: "#47cb38" }} />
									<div>
										<Title level={4} style={{ marginBottom: 8, color: "#1a1a1a" }}>
											Drag & Drop your video file here
										</Title>
										<Text type="secondary" style={{ fontSize: 14 }}>
											or click to browse from your computer
										</Text>
									</div>
									<Button icon={<UploadOutlined />}>Select Video File</Button>
									<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
										Supported formats: MP4, MOV, AVI, MKV (up to 30GB+)
									</Text>
								</Space>
							</Dragger>

							{file && (
								<FileInfo>
									<Text strong>Selected file: </Text>
									<Text>{file.name}</Text>
									<br />
									<Text strong>Size: </Text>
									<Text>{formatFileSize(file.size)}</Text>
								</FileInfo>
							)}

							{progress.status !== "idle" && (
								<ProgressContainer>
									<StatusText>
										{getStatusIcon()} {getStatusText()}
									</StatusText>
									<Progress
										percent={progress.percentage}
										status={
											progress.status === "error"
												? "exception"
												: progress.status === "success"
												? "success"
												: "active"
										}
										strokeColor={progress.status === "success" ? "#52c41a" : "#1890ff"}
									/>
									{progress.status === "uploading" && (
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
											{formatFileSize(progress.uploaded)} / {formatFileSize(progress.total)}
										</Text>
									)}
									{progress.status === "error" && progress.error && (
										<Alert
											message={progress.error}
											type="error"
											showIcon
											style={{ marginTop: 12 }}
										/>
									)}
								</ProgressContainer>
							)}

							<div style={{ marginTop: 24, textAlign: "center" }}>
								<Button
									type="primary"
									size="large"
									icon={<UploadOutlined />}
									onClick={handleUpload}
									disabled={!file || loading || progress.status === "uploading" || progress.status === "completing"}
									loading={fileUploadLoading}
								>
									{progress.status === "completing" ? "Completing..." : "Upload Videos"}
								</Button>
							</div>

							<Divider>OR</Divider>

							<UrlInputContainer>
								<Title level={5} style={{ marginBottom: 16 }}>
									<LinkOutlined style={{ marginRight: 8 }} />
									Ingest from Public URL
								</Title>
								<Space.Compact style={{ width: "100%" }}>
									<Input
										placeholder="Paste video URL here (e.g., https://example.com/video.mp4)"
										value={url}
										onChange={(e) => handleUrlChange(e.target.value)}
										disabled={loading}
										size="large"
										onPressEnter={handleIngest}
									/>
									<Button
										type="primary"
										size="large"
										icon={<LinkOutlined />}
										onClick={handleIngest}
										disabled={!url.trim() || loading}
										loading={urlIngestLoading}
									>
										Ingest
									</Button>
								</Space.Compact>
								<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
									Supports public video URLs. Extreme Reach URLs will be automatically resolved.
								</Text>
							</UrlInputContainer>
						</UploadCard>
					</Col>
				</Row>
			</Container>
		</StledSpin>
	);
};

export default VideoComplianceUpload;
